import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Layers, RefreshCw } from "lucide-react";
import { toast } from "react-toastify";
import { SpinLoader } from "../../../components/loader/Loader";
import { getSurveillanceHeatmap } from "../../../service/securityService";
import type { SurveillanceHeatmapResponse } from "../../../types/security_types";

const CrowdHeatmapScreen: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<SurveillanceHeatmapResponse | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const h = await getSurveillanceHeatmap();
      setData(h);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load heatmap");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const maxScore = Math.max(1, ...(data?.zones.map((z) => z.densityScore) || [1]));

  return (
    <div className="px-2 py-4 mt-14 min-h-screen space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/security/surveillance")}
            className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4" />
            Overview
          </button>
          <div className="bg-[#EAFBFA] p-3 rounded-lg">
            <Layers className="w-6 h-6 text-[#0fa9a6]" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Crowd density heatmap</h2>
            <p className="text-sm text-gray-600 max-w-2xl">
              Relative density by zone. When access zones exist, values come from granted access
              events in the last 24 hours; otherwise camera counts per location are shown.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => load()}
          className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-full text-sm hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {data?.methodology && (
        <p className="text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
          {data.methodology}
        </p>
      )}

      {loading ? (
        <div className="flex justify-center py-20">
          <SpinLoader />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {(data?.zones || []).map((z) => {
            const intensity = z.densityScore / maxScore;
            const hue = 200 - intensity * 120;
            return (
              <div
                key={z.zoneName + z.zoneId}
                className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm overflow-hidden"
              >
                <div className="flex justify-between items-start gap-2 mb-3">
                  <div>
                    <h3 className="font-semibold text-gray-900">{z.zoneName}</h3>
                    <p className="text-xs text-gray-500">
                      {z.accessGranted24h} events / coverage units (24h)
                    </p>
                  </div>
                  <span className="text-lg font-bold text-gray-800">{z.densityScore}</span>
                </div>
                <div
                  className="h-3 rounded-full overflow-hidden bg-gray-100"
                  title={`Density score ${z.densityScore}`}
                >
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${z.densityScore}%`,
                      background: `linear-gradient(90deg, hsl(${hue}, 70%, 45%), hsl(${hue}, 85%, 35%))`,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && data && data.zones.length === 0 && (
        <p className="text-center text-gray-500 text-sm py-12">
          No zone or camera location data yet. Add cameras or access zones.
        </p>
      )}
    </div>
  );
};

export default CrowdHeatmapScreen;
